import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../contexts/LanguageContext';

const DPA_LAST_UPDATED = '12 November 2025';

const dpaContent = {
  en: {
    title: 'Data Processing Agreement (Controller ↔ Processor)',
    backLabel: 'Back to Home',
    languageNote:
      'This DPA is provided in English and Spanish. In case of conflict between translations, the Spanish version prevails.',
    lastUpdated: `Last updated: ${DPA_LAST_UPDATED}`,
    sections: [
      {
        id: 'parties',
        title: '1. Parties & Roles',
        paragraphs: [
          'Processor: LiveLoveSpain ("LiveLoveSpain").',
          'Each Party is a separate and independent Controller for its own billing/account data; for client case data handled in the CRM, Agency is Controller and LiveLoveSpain is Processor.',
          'Data Protection Officer: info@livelovespain.com'
        ]
      },
      {
        id: 'definitions',
        title: '2. Definitions',
        paragraphs: [
          '\'Personal Data\' has the meaning in Art.4(1) GDPR. \'Service Data\' means data submitted to or generated in the Service by Controller or on its behalf, which may include Personal Data. \'Data Protection Laws\' means GDPR, LOPDGDD, LSSI-CE, and, where applicable, UK GDPR and other Member State laws. \'SCCs\' means EU Commission Decision (EU) 2021/914, Modules 2 and 3.'
        ]
      },
      {
        id: 'term',
        title: '3. Acceptance, Effective Date & Term',
        paragraphs: [
          'This DPA forms part of the Agreement (including the Terms of Service) between Controller and Processor. It becomes binding on Controller when Controller creates an account, enables the Service, or otherwise accepts the Agreement ("Effective Date").',
          'This DPA remains in force for the duration of Processor’s processing of Personal Data on behalf of Controller and until deletion in accordance with Section 12.',
          'This DPA is concluded electronically without signatures; no wet-ink signatures are required for validity.'
        ]
      },
      {
        id: 'precedence',
        title: '3A. Order of Precedence',
        paragraphs: [
          'In the event of conflict: (i) the Standard Contractual Clauses (SCCs) prevail over this DPA and the Agreement; (ii) this DPA prevails over the Agreement; and (iii) the Agreement prevails over other documents.'
        ]
      },
      {
        id: 'subject',
        title: '4. Subject Matter & Purpose',
        paragraphs: [
          'Processor will process Service Data (client files, forms, IDs, tasks, messages, etc.) solely to provide the CRM and related support in accordance with Controller\'s documented instructions and this DPA.'
        ]
      },
      {
        id: 'controller-resp',
        title: '4A. Controller Responsibilities',
        paragraphs: [
          'Controller is solely responsible for (a) the lawfulness of Personal Data processing and providing all required notices to data subjects; (b) establishing a valid legal basis; (c) configuring the Service (roles/permissions, retention, regions) and ensuring data minimisation; and (d) not introducing Personal Data that is unrelated to the Service purposes.'
        ]
      },
      {
        id: 'restricted-data',
        title: '4B. Restricted Categories of Data',
        paragraphs: [
          'Unless expressly enabled by in-app configuration and protected by appropriate safeguards, Controller shall not submit: special categories of data (Art. 9 GDPR), data relating to criminal convictions/offences (Art. 10), biometric templates used for identification, genetic data, precise geolocation beyond what is necessary, or data of children under 16. If Controller enables any such categories, Controller represents it has a lawful basis and required safeguards in place. If local law sets a different age of consent, Controller is responsible for compliance.'
        ]
      },
      {
        id: 'instructions',
        title: '5. Instructions',
        paragraphs: [
          'Processor shall process Personal Data only:',
          '1. as necessary to provide the Service;',
          '2. per Controller\'s in-app configuration and written instructions; and',
          '3. as required by EU/Member State law (Processor will inform Controller unless law forbids).'
        ]
      },
      {
        id: 'government-requests',
        title: '5A. Government Requests',
        paragraphs: [
          'Processor will (a) promptly notify Controller of any legally binding request from public authorities to disclose Personal Data, unless legally prohibited; (b) assess and, where reasonable, challenge unlawful or over-broad requests; and (c) disclose only the minimum necessary.'
        ]
      },
      {
        id: 'confidentiality',
        title: '6. Confidentiality',
        paragraphs: [
          'Processor ensures confidentiality by binding staff to appropriate obligations.'
        ]
      },
      {
        id: 'security',
        title: '7. Security',
        paragraphs: [
          'Processor implements appropriate technical and organisational measures (TOMs) described in Annex II (incl. encryption in transit/at rest, RBAC, least privilege, logging, backups, vulnerability management). Processor maintains controls aligned to recognised frameworks (e.g., ISO 27001/27002) appropriate for a SaaS SME.'
        ]
      },
      {
        id: 'data-location',
        title: '7A. Data Location',
        paragraphs: [
          'Processor hosts primary data in EEA data centres unless otherwise specified in the Agreement or selected by Controller via in-app configuration. Certain CDN/edge, observability or security layers may transiently process metadata outside the EEA under Section 8 safeguards; primary data at rest remains in the EEA. Certain support operations (e.g., 24/7 on-call) may access data from outside the EEA subject to Section 8 and the SCCs.'
        ]
      },
      {
        id: 'subprocessors',
        title: '8. Sub-Processors',
        paragraphs: [
          'Controller provides general authorisation to engage sub-processors listed in Annex III and the live list at https://www.livelovespain.com/crm/subprocessors (which includes a change log).',
          'Processor will impose data protection obligations no less protective than this DPA (Art. 28(4)).',
          'Processor will notify Controller of intended changes at least 15 days before replacement/addition via email or in-app notice. Controller may object on reasonable grounds relating to data protection within that period. If the Parties cannot agree, Controller\'s sole remedy is to suspend the affected integration or terminate the affected Service component, with a pro-rated refund of unused fees for the affected component.'
        ]
      },
      {
        id: 'transfers',
        title: '9. International Transfers & SCCs',
        paragraphs: [
          'Where Personal Data is transferred outside the EEA/UK to a country without an adequacy decision, the European Commission Standard Contractual Clauses (SCCs) (Module Two Controller→Processor and Module Three Processor→Sub-processor) are incorporated by reference. The text of the SCCs (EU 2021/914) is incorporated by reference; the Parties agree the Docking Clause applies. The Annexes to this DPA populate the SCC appendices.',
          'Processor will (a) conduct and/or support a transfer impact assessment upon reasonable request, and (b) implement supplementary measures where required.',
          'Where applicable, the EU–U.S. Data Privacy Framework, its extensions, or other valid safeguards under Articles 44–49 GDPR may be used.',
          'For Switzerland, references to GDPR/Member State law shall be read to include FADP/FDPIC. For UK data, the UK Addendum/IDTA governs.'
        ]
      },
      {
        id: 'assistance',
        title: '10. Assistance (DSRs, DPIAs, and Security)',
        paragraphs: [
          'Taking into account the nature of processing, Processor will assist Controller in responding to data subject requests (Arts. 12–23) and with DPIAs/consultations (Arts. 35–36). Processor will route in-product DSRs to Controller and, upon request, provide commercially reasonable assistance within 10 business days.',
          'Controller is responsible for verifying identity and determining whether a request should be fulfilled. Where requests are manifestly unfounded or excessive, Processor may charge a reasonable fee for support or decline to act.',
          'Processor will provide available product features and documentation to facilitate Controller’s compliance duties.'
        ]
      },
      {
        id: 'breach',
        title: '11. Personal Data Breach',
        paragraphs: [
          'Processor will notify Controller without undue delay after becoming aware of a Personal Data Breach affecting Service Data, and provide information reasonably required to support Controller’s obligations (including the 72-hour notification duty under Article 33 GDPR).',
          'Notification channels: info@livelovespain.com (or as otherwise configured in the Service).'
        ]
      },
      {
        id: 'audits',
        title: '12. Audits',
        paragraphs: [
          'Upon written request once per 12 months (or after a material incident), Processor will provide audit reports or summaries (e.g., external assessments, penetration tests) and answer reasonable questionnaires. Where insufficient, Controller may perform an on-site audit on reasonable notice, at Controller\'s cost, without disrupting other tenants\' security or confidentiality.',
          'Audits are limited to facilities, systems and records relevant to the Service; require 30 days\' prior notice (except emergency); and may occur once every 12 months unless required by law/regulator or following a material incident.'
        ]
      },
      {
        id: 'return',
        title: '13. Return & Deletion',
        paragraphs: [
          'Upon termination or upon request, Processor will make Personal Data available for export in industry-standard formats via self-service tools or secure transfer. Unless legally required to retain it, Processor will delete remaining Personal Data within 30 days after termination.',
          'Encrypted backups are purged on a rolling basis within 90 days. Immutable security/audit logs may be retained for security and compliance for up to 12 months and then deleted or anonymised.',
          'On request, Processor will provide a written certificate of deletion.'
        ]
      },
      {
        id: 'multitenancy',
        title: '14. Multi-Tenancy & Shared Responsibility',
        paragraphs: [
          'Processor operates a multi-tenant SaaS. Controller is responsible for:',
          '• configuring roles/permissions, retention, and regions where applicable;',
          '• obtaining consents and providing notices to data subjects;',
          '• uploading only data that has a lawful basis and complying with the restrictions in Section 4B when special categories are introduced.',
          'If Controller embeds the client portal on its own website, cookie banners/consent on that website are Controller\'s responsibility.'
        ]
      },
      {
        id: 'liability',
        title: '15. Liability',
        paragraphs: [
          'Liability follows the Agreement\'s caps and exclusions.'
        ]
      },
      {
        id: 'governing',
        title: '16. Governing Law & Jurisdiction',
        paragraphs: [
          'This DPA is governed by Spanish law; courts of Madrid have jurisdiction (without prejudice to SCC forum clauses where applicable).'
        ]
      }
    ],
    annexes: [
      {
        id: 'annex1',
        title: 'Annex I – Details of Processing',
        sections: [
          {
            title: 'A. Categories of Data Subjects',
            content: 'Clients (expats/assignees), client family members, Agency staff and contractors using the CRM.'
          },
          {
            title: 'B. Categories of Personal Data',
            content: 'Identification data (name, contact, IDs), immigration/workflow data, documents (passports, visas, proofs), communications, usage logs, metadata. Special categories may be processed only if Controller instructs and configures safeguards (e.g., health notes for suitability of move).'
          },
          {
            title: 'C. Processing Operations & Purpose',
            content: 'Hosting, storage, transmission, indexing, display, search, collaboration, workflow automation, support, analytics on usage (aggregated/metadata).'
          },
          {
            title: 'D. Duration',
            content: 'Term of the Agreement + retention windows in Section 12.'
          },
          {
            title: 'E. Controller\'s Instructions',
            content: 'As set via in-app settings, APIs, written tickets; no processing for Processor\'s marketing.'
          }
        ]
      },
      {
        id: 'annex2',
        title: 'Annex II – Technical & Organisational Measures (TOMs)',
        intro: 'Processor may update these TOMs from time to time without materially reducing overall security.',
        list: [
          'Governance: written security policy; risk assessments at least annually; mandatory security & privacy training; quarterly access reviews; vendor due diligence.',
          'Access Control: SSO/OAuth (if enabled), MFA (per plan or mandatory for admins), RBAC with least privilege; IP allowlists (enterprise); session timeout & revocation.',
          'Encryption: TLS 1.2+ in transit; AES-256 at rest; KMS-managed keys; HSTS and perfect forward secrecy where supported.',
          'Segregation: logical tenant isolation at app and data layers; strict query scoping; per-tenant identifiers; data access approved and logged.',
          'Hardening & Monitoring: container hardening; vulnerability scans (at least monthly) and patch SLAs; IDS/IPS at provider; centralised logs; immutable admin audit logs (≥12 months).',
          'Availability: multi-AZ hosting; daily automated backups; DR objectives: RPO ≤24h, RTO ≤24h; tested at least annually.',
          'Change Management: version control; peer code review; CI/CD with automated checks; staged rollouts and rollback plans.',
          'Incident Response: playbooks; on-call rotation; customer comms templates; post-incident reviews with corrective actions.',
          'Data Minimisation & Retention: configurable retention; secure deletion; media sanitisation per NIST 800-88 or equivalent.',
          'Vendor Management: sub-processor DPAs; security review before onboarding; annual reassessment; continuous monitoring when feasible.'
        ]
      },
      {
        id: 'annex3',
        title: 'Annex III – Sub-processors',
        paragraphs: [
          'Current sub-processors:',
          '• Vercel Inc. – Application hosting and infrastructure. Location: Global edge network (primary regions: EU/US). Safeguards: DPA in place; data processed in accordance with Vercel\'s data processing agreement.',
          '• MongoDB Atlas (MongoDB Inc.) – Database hosting and storage. Location: [Specify your MongoDB Atlas region, e.g., EU (Ireland) or EU (Frankfurt)]. Safeguards: SCCs; DPA in place; encryption at rest and in transit.',
          '• Mailgun Technologies EU – Transactional email delivery. Location: EU (Frankfurt). Safeguards: SCCs; DPA in place.',
          '• Stripe Payments Europe Ltd. – Billing and payments (independent controller). Location: EU / US. Safeguards: EU-U.S. DPF; SCCs. Note: Stripe acts as an independent Controller for payment data; not a sub-processor for client case data.'
        ]
      },
      {
        id: 'annex4',
        title: 'Annex IV – Data Breach Notification Details',
        paragraphs: [
          '• Report channels: info@livelovespain.com',
          '• Minimum content: nature of breach, categories/volume, likely consequences, measures taken, contact point.'
        ]
      }
    ],
    signatures: {
      title: 'Acceptance',
      note: 'This DPA is incorporated into and forms part of the Agreement (including the Terms of Service). It is concluded electronically and is binding on Controller upon account creation, enabling the Service, or otherwise accepting the Agreement.'
    }
  },
  es: {
    title: 'Acuerdo de Encargado de Tratamiento (RGPD)',
    backLabel: 'Volver al Inicio',
    languageNote:
      'Este DPA se proporciona en inglés y español. En caso de conflicto entre traducciones, prevalece la versión española.',
    lastUpdated: `Última actualización: 12 de noviembre de 2025`,
    sections: [
      {
        id: 'partes',
        title: '1. Partes y Roles',
        paragraphs: [
          'Encargado: LiveLoveSpain ("LiveLoveSpain").',
          'Cada parte es Responsable independiente de sus propios datos de facturación/cuenta; respecto de los datos de casos de clientes en el CRM, la Agencia es Responsable y LiveLoveSpain es Encargado.',
          'Delegado de Protección de Datos: info@livelovespain.com'
        ]
      },
      {
        id: 'definiciones',
        title: '2. Definiciones',
        paragraphs: [
          '\'Datos Personales\' tiene el significado del art.4(1) RGPD. \'Datos del Servicio\' significa datos remitidos o generados en el Servicio por el Responsable o en su nombre, que pueden incluir Datos Personales. \'Leyes de Protección de Datos\' significa RGPD, LOPDGDD, LSSI-CE y, cuando sea aplicable, UK GDPR y otras leyes de Estados miembros. \'CCT\' significa la Decisión de la Comisión Europea (UE) 2021/914, Módulos 2 y 3.'
        ]
      },
      {
        id: 'vigencia',
        title: '3. Aceptación, Fecha de Efecto y Vigencia',
        paragraphs: [
          'Este DPA forma parte del Contrato (incluidas las Condiciones de Servicio) entre Responsable y Encargado. Queda vinculado cuando el Responsable crea una cuenta, habilita el Servicio o acepta el Contrato ("Fecha de Efecto").',
          'Permanecerá vigente mientras el Encargado trate Datos Personales por cuenta del Responsable y hasta su eliminación conforme a la Sección 13.',
          'Se celebra electrónicamente sin necesidad de firmas manuscritas.'
        ]
      },
      {
        id: 'prelacion',
        title: '3A. Orden de Prelación',
        paragraphs: [
          'En caso de conflicto: (i) las Cláusulas Contractuales Tipo prevalecen sobre este DPA y el Contrato; (ii) este DPA prevalece sobre el Contrato; y (iii) el Contrato prevalece sobre otros documentos.'
        ]
      },
      {
        id: 'objeto',
        title: '4. Objeto y Finalidad',
        paragraphs: [
          'Tratamiento de Datos del Servicio únicamente para prestar el CRM y soporte, conforme a instrucciones documentadas del Responsable.'
        ]
      },
      {
        id: 'responsable-obligaciones',
        title: '4A. Responsabilidades del Responsable',
        paragraphs: [
          'El Responsable asume en exclusiva: (a) la licitud del tratamiento y la entrega de todos los avisos a los interesados; (b) la existencia de una base jurídica válida; (c) la configuración del Servicio (roles/permisos, retención, regiones) garantizando la minimización; y (d) no introducir Datos Personales ajenos a las finalidades del Servicio.'
        ]
      },
      {
        id: 'datos-restringidos',
        title: '4B. Categorías de Datos Restringidas',
        paragraphs: [
          'Salvo que se habilite expresamente mediante configuración y con salvaguardas apropiadas, el Responsable no remitirá: categorías especiales de datos (art. 9 RGPD), datos relativos a condenas e infracciones penales (art. 10), plantillas biométricas para identificación, datos genéticos, geolocalización precisa más allá de lo necesario, ni datos de menores de 16 años. Si habilita alguna de estas categorías, declara disponer de base jurídica y medidas necesarias. Si la ley local establece una edad de consentimiento diferente, el Responsable es responsable del cumplimiento.'
        ]
      },
      {
        id: 'instrucciones',
        title: '5. Instrucciones',
        paragraphs: [
          'El Encargado tratará datos únicamente para prestar el Servicio, conforme a la configuración de la Agencia y a lo exigido por la normativa aplicable. Cuando una norma obligue al Encargado a tratar datos más allá de las instrucciones, lo notificará al Responsable salvo prohibición legal.'
        ]
      },
      {
        id: 'solicitudes-gubernamentales',
        title: '5A. Solicitudes Gubernamentales',
        paragraphs: [
          'El Encargado (a) notificará sin dilación al Responsable de cualquier solicitud legalmente vinculante de autoridades públicas para revelar Datos Personales, salvo prohibición legal; (b) evaluará y, cuando sea razonable, impugnará solicitudes ilegales o excesivamente amplias; y (c) revelará solo lo mínimo necesario.'
        ]
      },
      {
        id: 'confidencialidad',
        title: '6. Confidencialidad',
        paragraphs: [
          'Personal sujeto a deber de secreto y compromisos adecuados.'
        ]
      },
      {
        id: 'seguridad',
        title: '7. Seguridad',
        paragraphs: [
          'Medidas técnicas y organizativas en Anexo II (cifrado, RBAC, mínimos privilegios, registros, copias, gestión de vulnerabilidades). El Encargado mantiene controles alineados con marcos reconocidos (p. ej., ISO 27001/27002) apropiados para una PYME SaaS.'
        ]
      },
      {
        id: 'ubicacion-datos',
        title: '7A. Ubicación de Datos',
        paragraphs: [
          'El alojamiento principal se realiza en centros de datos del EEE salvo que el Contrato disponga otra cosa o el Responsable seleccione una región distinta en la aplicación. Ciertas capas de CDN/edge, observabilidad o seguridad pueden procesar transitoriamente metadatos fuera del EEE bajo las salvaguardas de la Sección 8; los datos primarios en reposo permanecen en el EEE. Determinadas operaciones de soporte (p. ej., guardias 24/7) pueden acceder desde fuera del EEE conforme a la Sección 8 y a las CCT.'
        ]
      },
      {
        id: 'subencargados',
        title: '8. Subencargados',
        paragraphs: [
          'Autorización general a los subencargados del Anexo III y la lista viva en https://www.livelovespain.com/crm/subprocessors (que incluye un registro de cambios).',
          'El Encargado impondrá obligaciones de protección de datos al menos tan exigentes como las de este DPA (art. 28.4).',
          'Avisará de cambios con al menos 15 días de antelación mediante correo electrónico o aviso en la aplicación. La Agencia podrá oponerse por motivos razonables relacionados con la protección de datos en ese plazo. Si no se alcanza acuerdo, el único remedio será suspender la integración afectada o resolver el componente del Servicio correspondiente, con un reembolso prorrateado de las tarifas no utilizadas para el componente afectado.'
        ]
      },
      {
        id: 'transferencias',
        title: '9. Transferencias Internacionales y CCT',
        paragraphs: [
          'Cuando los Datos Personales se transfieran fuera del EEE/RU a países sin decisión de adecuación, se incorporan por referencia las Cláusulas Contractuales Tipo (CCT) de la Comisión Europea (Módulo 2 Responsable→Encargado y Módulo 3 Encargado→Subencargado). El texto de las CCT (UE 2021/914) se incorpora por referencia; las Partes acuerdan que se aplica la Cláusula de Acoplamiento. Los Anexos de este DPA completan las CCT.',
          'El Encargado realizará y/o colaborará en evaluaciones de impacto de la transferencia cuando se le solicite razonablemente e implementará medidas complementarias cuando sean necesarias.',
          'Podrán emplearse, cuando resulte aplicable, el Marco de Privacidad de Datos UE–EE. UU., sus extensiones u otras garantías válidas conforme a los artículos 44–49 RGPD.',
          'Para Suiza, las referencias al RGPD/ley de Estados miembros se entenderán para incluir FADP/FDPIC. Para datos del Reino Unido, rige el Addendum/IDTA del Reino Unido.'
        ]
      },
      {
        id: 'asistencia',
        title: '10. Asistencia (Derechos, EIPD y Seguridad)',
        paragraphs: [
          'Teniendo en cuenta la naturaleza del tratamiento, el Encargado ayudará al Responsable a responder solicitudes de interesados (arts. 12–23) y en la realización de EIPD/consultas (arts. 35–36). El Encargado dirigirá las solicitudes de derechos de interesados (DSR) en el producto al Responsable y, a solicitud, proporcionará asistencia comercialmente razonable dentro de 10 días hábiles.',
          'El Responsable verificará la identidad y decidirá si corresponde atender cada solicitud. Cuando sean manifiestamente infundadas o excesivas, el Encargado podrá cobrar un cargo razonable por la asistencia o negarse a actuar.',
          'El Encargado facilitará las funcionalidades y documentación disponibles que apoyen el cumplimiento del Responsable.'
        ]
      },
      {
        id: 'violaciones',
        title: '11. Violaciones de Seguridad',
        paragraphs: [
          'El Encargado notificará sin dilación indebida tras conocer una Violación de Datos Personales que afecte a los Datos del Servicio, aportando la información razonablemente necesaria para que el Responsable cumpla sus obligaciones (incluido el plazo de 72 horas del artículo 33 RGPD).',
          'Canales de notificación: info@livelovespain.com (u otros configurados en el Servicio).'
        ]
      },
      {
        id: 'auditorias',
        title: '12. Auditorías',
        paragraphs: [
          'Informes y respuestas a cuestionarios razonables. Si es insuficiente, auditoría in situ con preaviso y a cargo de la Agencia, sin comprometer la confidencialidad/seguridad de otros arrendatarios.',
          'Las auditorías se limitan a instalaciones, sistemas y registros relevantes para el Servicio; requieren un preaviso de 30 días (excepto emergencias); y pueden ocurrir una vez cada 12 meses a menos que lo requiera la ley/regulador o tras un incidente material.'
        ]
      },
      {
        id: 'devolucion',
        title: '13. Devolución y Supresión',
        paragraphs: [
          'Al finalizar la relación o cuando lo solicite el Responsable, el Encargado pondrá los Datos Personales a disposición para su exportación en formatos estándar mediante herramientas de autoservicio o transferencia segura. Salvo obligación legal, eliminará los datos restantes dentro de los 30 días posteriores.',
          'Las copias de seguridad cifradas se purgan de forma rotativa dentro de 90 días. Los registros de seguridad/auditoría inmutables podrán conservarse por motivos de seguridad y cumplimiento hasta 12 meses, tras lo cual se eliminarán o anonimizarán.',
          'A solicitud, el Encargado proporcionará un certificado escrito de eliminación.'
        ]
      },
      {
        id: 'multicliente',
        title: '14. Multicliente y Responsabilidades',
        paragraphs: [
          'El CRM es multicliente. La Agencia configura permisos, retenciones y base jurídica; debe cumplir lo dispuesto en la Sección 4B cuando introduzca categorías especiales.',
          'Si el Responsable integra el portal del cliente en su propio sitio web, los banners de cookies/consentimiento en ese sitio web son responsabilidad del Responsable.'
        ]
      },
      {
        id: 'responsabilidad',
        title: '15. Responsabilidad',
        paragraphs: [
          'La responsabilidad se rige por los límites y exclusiones del Contrato.'
        ]
      },
      {
        id: 'ley',
        title: '16. Ley y Jurisdicción',
        paragraphs: [
          'Ley española y Juzgados de Madrid (sin perjuicio de lo previsto en las CCT).'
        ]
      }
    ],
    annexes: [
      {
        id: 'anexo1',
        title: 'Anexo I – Detalles del Tratamiento',
        sections: [
          {
            title: 'A. Categorías de Interesados',
            content: 'Clientes (expatriados/asignados), familiares de clientes, personal y contratistas de la Agencia que utilizan el CRM.'
          },
          {
            title: 'B. Categorías de Datos Personales',
            content: 'Datos de identificación (nombre, contacto, DNI), datos de inmigración/flujos de trabajo, documentos (pasaportes, visados, pruebas), comunicaciones, registros de uso, metadatos. Las categorías especiales solo se procesarán si el Responsable lo instruye y configura salvaguardas (p. ej., notas de salud para idoneidad del traslado).'
          },
          {
            title: 'C. Operaciones de Tratamiento y Finalidad',
            content: 'Alojamiento, almacenamiento, transmisión, indexación, visualización, búsqueda, colaboración, automatización de flujos, soporte, análisis de uso (agregados/metadatos).'
          },
          {
            title: 'D. Duración',
            content: 'Vigencia del Contrato + ventanas de retención en la Sección 12.'
          },
          {
            title: 'E. Instrucciones del Responsable',
            content: 'Según configuración en la aplicación, APIs, tickets escritos; sin tratamiento para marketing del Encargado.'
          }
        ]
      },
      {
        id: 'anexo2',
        title: 'Anexo II – Medidas Técnicas y Organizativas (MTO)',
        intro: 'El Encargado puede actualizar estas MTO de vez en cuando sin reducir materialmente la seguridad general.',
        list: [
          'Gobernanza: política de seguridad documentada; evaluaciones de riesgo al menos anuales; formación obligatoria en seguridad y privacidad; revisiones trimestrales de accesos; diligencia debida a proveedores.',
          'Control de Acceso: SSO/OAuth (si está habilitado), MFA (según plan o obligatorio para administradores), RBAC con mínimo privilegio; listas de IP permitidas (enterprise); caducidad y revocación de sesiones.',
          'Cifrado: TLS 1.2+ en tránsito; AES-256 en reposo; claves gestionadas por KMS; HSTS y secreto perfecto directo cuando proceda.',
          'Segregación: aislamiento lógico por inquilino en aplicación y datos; alcance estricto de consultas; identificadores por inquilino; accesos aprobados y registrados.',
          'Endurecimiento y Monitoreo: hardening de contenedores; escaneos de vulnerabilidades (al menos mensuales) y SLAs de parcheo; IDS/IPS del proveedor; registros centralizados; logs de auditoría de administración inmutables (≥12 meses).',
          'Disponibilidad: alojamiento multi-AZ; copias de seguridad automatizadas diarias; objetivos de DR: RPO ≤24h, RTO ≤24h; pruebas al menos anuales.',
          'Gestión de Cambios: control de versiones; revisión de código por pares; CI/CD con verificaciones automáticas; despliegues escalonados y planes de reversión.',
          'Respuesta a Incidentes: manuales de actuación; rotación de guardias; plantillas de comunicación con clientes; revisiones post-incidente con acciones correctoras.',
          'Minimización y Retención: retención configurable; eliminación segura; sanitización de soportes conforme a NIST 800-88 o equivalente.',
          'Gestión de Proveedores: acuerdos de encargado; revisión de seguridad antes de la incorporación; reevaluación anual; monitorización continua cuando sea viable.'
        ]
      },
      {
        id: 'anexo3',
        title: 'Anexo III – Subencargados',
        paragraphs: [
          'Subencargados actuales:',
          '• Vercel Inc. – Alojamiento de aplicaciones e infraestructura. Ubicación: Red global de edge (regiones principales: UE/EE. UU.). Garantías: DPA en vigor; datos procesados conforme al acuerdo de procesamiento de datos de Vercel.',
          '• MongoDB Atlas (MongoDB Inc.) – Alojamiento y almacenamiento de base de datos. Ubicación: [Especifique su región de MongoDB Atlas, p. ej., UE (Irlanda) o UE (Frankfurt)]. Garantías: CCT; DPA en vigor; cifrado en reposo y en tránsito.',
          '• Mailgun Technologies EU – Entrega de correo transaccional. Ubicación: UE (Frankfurt). Garantías: CCT; DPA en vigor.',
          '• Stripe Payments Europe Ltd. – Facturación y pagos (responsable independiente). Ubicación: UE / EE. UU. Garantías: Marco de Privacidad de Datos UE-EE. UU.; CCT. Nota: Stripe actúa como Responsable independiente para datos de pago; no es un subencargado para datos de casos de clientes.'
        ]
      },
      {
        id: 'anexo4',
        title: 'Anexo IV – Detalles de Notificación de Violaciones',
        paragraphs: [
          '• Canales de reporte: info@livelovespain.com',
          '• Contenido mínimo: naturaleza de la violación, categorías/volumen, consecuencias probables, medidas adoptadas, punto de contacto.'
        ]
      }
    ],
    signatures: {
      title: 'Aceptación',
      note: 'Este DPA se incorpora al Contrato (incluidas las Condiciones de Servicio). Se celebra electrónicamente y vincula a la Agencia al crear una cuenta, habilitar el Servicio o aceptar el Contrato.'
    }
  }
};

const DPA = () => {
  const { currentLanguage } = useLanguage();
  const isSpanish = currentLanguage === 'es';
  const content = isSpanish ? dpaContent.es : dpaContent.en;
  const relatedLabel = isSpanish ? 'Documentos relacionados:' : 'Related:';
  const termsLabel = isSpanish ? 'Condiciones' : 'Terms';
  const privacyLabel = isSpanish ? 'Privacidad' : 'Privacy';
  const cookiesLabel = isSpanish ? 'Cookies' : 'Cookies';
  const securityLabel = isSpanish ? 'Seguridad' : 'Security';
  const tocLabel = isSpanish ? 'Índice' : 'Table of Contents';
  const annexesLabel = isSpanish ? 'Anexos' : 'Annexes';

  // Get canonical URL based on hostname
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isSubdomain = hostname === 'crm.livelovespain.com' || hostname.startsWith('crm.');
  const canonicalUrl = isSubdomain 
    ? 'https://crm.livelovespain.com/dpa'
    : 'https://www.livelovespain.com/crm/dpa';

  // Add JSON-LD schemas
  React.useEffect(() => {
    const baseUrl = isSubdomain ? 'https://crm.livelovespain.com' : 'https://www.livelovespain.com/crm';
    
    // Breadcrumbs Schema
    const breadcrumbsId = 'jsonld-breadcrumbs';
    let breadcrumbsScript = document.getElementById(breadcrumbsId);
    const breadcrumbsPayload = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": baseUrl + "/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Data Processing Agreement",
          "item": canonicalUrl
        }
      ]
    };
    if (!breadcrumbsScript) {
      breadcrumbsScript = document.createElement('script');
      breadcrumbsScript.type = 'application/ld+json';
      breadcrumbsScript.id = breadcrumbsId;
      document.head.appendChild(breadcrumbsScript);
    }
    breadcrumbsScript.text = JSON.stringify(breadcrumbsPayload);

    // WebPage Schema
    const webpageId = 'jsonld-webpage';
    let webpageScript = document.getElementById(webpageId);
    const webpagePayload = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Data Processing Agreement (DPA) - LiveLoveSpain CRM",
      "description": "Data Processing Agreement for LiveLoveSpain CRM. GDPR-compliant data processing agreement outlining how we handle your data.",
      "url": canonicalUrl,
      "inLanguage": "en",
      "isPartOf": {
        "@type": "WebSite",
        "name": "LiveLoveSpain CRM",
        "url": baseUrl
      },
      "about": {
        "@type": "Thing",
        "name": "Data Processing Agreement"
      },
      "publisher": {
        "@type": "Organization",
        "name": "LiveLoveSpain"
      }
    };
    if (!webpageScript) {
      webpageScript = document.createElement('script');
      webpageScript.type = 'application/ld+json';
      webpageScript.id = webpageId;
      document.head.appendChild(webpageScript);
    }
    webpageScript.text = JSON.stringify(webpagePayload);

    return () => {
      const breadcrumbs = document.getElementById(breadcrumbsId);
      const webpage = document.getElementById(webpageId);
      if (breadcrumbs) breadcrumbs.remove();
      if (webpage) webpage.remove();
    };
  }, [canonicalUrl, isSubdomain]);

  return (
    <>
      <Helmet>
        <title>Data Processing Agreement (DPA) - LiveLoveSpain CRM</title>
        <meta name="description" content="Data Processing Agreement for LiveLoveSpain CRM. GDPR-compliant data processing agreement outlining how we handle your data." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Data Processing Agreement (DPA) - LiveLoveSpain CRM" />
        <meta property="og:description" content="Data Processing Agreement for LiveLoveSpain CRM. GDPR-compliant data processing agreement outlining how we handle your data." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Data Processing Agreement (DPA) - LiveLoveSpain CRM" />
        <meta name="twitter:description" content="Data Processing Agreement for LiveLoveSpain CRM. GDPR-compliant data processing agreement outlining how we handle your data." />
      </Helmet>
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          {content.backLabel}
        </Link>
        
        <div className="bg-card rounded-xl shadow-lg p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            {content.title}
          </h1>
          
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-muted-foreground mb-4">
              {content.lastUpdated}
            </p>
            <p className="text-sm text-muted-foreground italic mb-6">
              {content.languageNote}
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              {isSpanish
                ? 'Versiones anteriores del DPA están disponibles a solicitud en info@livelovespain.com.'
                : 'Previous versions of this DPA are available on request via info@livelovespain.com.'}
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              {relatedLabel}{' '}
              <Link to="/terms" className="text-blue-600 hover:text-blue-500">{termsLabel}</Link> ·{' '}
              <a href="/privacy" className="text-blue-600 hover:text-blue-500">{privacyLabel}</a> ·{' '}
              <a href="/cookies" className="text-blue-600 hover:text-blue-500">{cookiesLabel}</a> ·{' '}
              <a href="/security" className="text-blue-600 hover:text-blue-500">{securityLabel}</a>
            </p>

            <nav className="bg-muted/40 rounded-lg p-4 mb-8">
              <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
                {tocLabel}
              </h2>
              <ul className="grid gap-2 md:grid-cols-2 text-sm">
                {content.sections.map((section) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} className="text-blue-600 hover:text-blue-500">
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            
            {content.sections.map((section) => (
              <section key={section.id} className="mb-8" id={section.id}>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  {section.title}
                </h2>
                {section.paragraphs.map((para, idx) => (
                  <p key={idx} className="text-muted-foreground leading-relaxed mb-3">
                    {para}
                  </p>
                ))}
              </section>
            ))}

            <div className="mt-12 mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-6">
                {annexesLabel}
              </h2>
              
              {content.annexes.map((annex) => (
                <section key={annex.id} className="mb-8" id={annex.id}>
                  <h3 className="text-xl font-semibold text-foreground mb-4">
                    {annex.title}
                  </h3>
                  {annex.intro && (
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      {annex.intro}
                    </p>
                  )}
                  {annex.sections && annex.sections.map((subSection, idx) => (
                    <div key={idx} className="mb-4">
                      <h4 className="text-lg font-medium text-foreground mb-2">
                        {subSection.title}
                      </h4>
                      <p className="text-muted-foreground leading-relaxed">
                        {subSection.content}
                      </p>
                    </div>
                  ))}
                  {annex.list && (
                    <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                      {annex.list.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {annex.paragraphs && annex.paragraphs.map((para, idx) => (
                    <p key={idx} className="text-muted-foreground leading-relaxed mb-3">
                      {para}
                    </p>
                  ))}
                </section>
              ))}
            </div>

            <section className="mt-12 mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {content.signatures.title}
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {content.signatures.note}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default DPA;

